import React, { lazy } from "react";
import { Navigate, Outlet, Route } from "react-router-dom";
import MainLayout from "@/components/MainLayout/MainLayout";
import { AuthenticationGuard } from "@/components/AuthenticationGuard/AuthenticationGuard";
import PermissionGuard from "@/components/PermissionGuard/PermissionGuard";

// Lazy load all route components
const CourseList = lazy(() =>
  import(
    "@/modules/learningCenter/features/CourseManagement/components/CourseList/CourseList"
  ).then((m) => ({ default: m.CourseList })),
);
const CourseEditor = lazy(() =>
  import(
    "@/modules/learningCenter/features/CourseManagement/components/CourseEditor/CourseEditor"
  ).then((m) => ({ default: m.CourseEditor })),
);
const CourseDetailPage = lazy(() =>
  import(
    "@/modules/learningCenter/features/CourseDirectory/components/CourseDetailPage/CourseDetailPage"
  ).then((m) => ({ default: m.CourseDetailPage })),
);
const SkillList = lazy(() =>
  import(
    "@/modules/learningCenter/features/SkillManagement/components/SkillList/SkillList"
  ).then((m) => ({ default: m.SkillList })),
);
const QuizList = lazy(() =>
  import(
    "@/modules/learningCenter/features/QuizManagement/components/QuizList/QuizList"
  ).then((m) => ({ default: m.QuizList })),
);
const QuizMaker = lazy(() =>
  import(
    "@/modules/learningCenter/features/QuizManagement/components/QuizMaker"
  ).then((m) => ({ default: m.QuizMaker })),
);
const CourseCategoryList = lazy(() =>
  import(
    "@/modules/learningCenter/features/CategoryManagement/components/CourseCategoryList/CourseCategoryList"
  ).then((m) => ({ default: m.CourseCategoryList })),
);
const LearningPaths = lazy(() =>
  import("@/modules/learningCenter/features/LearningPaths").then((m) => ({
    default: m.LearningPaths,
  })),
);
const CoachesLearning = lazy(
  () =>
    import("@/modules/learningCenter/features/CoachesLearning/CoachesLearning"),
);

const MyLearning = lazy(
  () => import("@/modules/learningCenter/features/MyLearning/MyLearning"),
);

const LearningDirectory = lazy(
  () =>
    import(
      "@/modules/learningCenter/features/LearningDirectory/LearningDirectory"
    ),
);

const PathViewer = lazy(() =>
  import(
    "@/modules/learningCenter/features/LearningPathViewer/components/PathViewer"
  ).then((m) => ({ default: m.PathViewer })),
);
const BadgeManagement = lazy(() =>
  import(
    "@/modules/learningCenter/features/BadgeManagement/BadgeManagement"
  ).then((m) => ({ default: m.BadgeManagement })),
);
const CustomChecklistTaskManagement = lazy(() =>
  import(
    "@/modules/learningCenter/features/LearningPaths/components/Admin/CustomChecklistTaskManagement"
  ).then((m) => ({ default: m.CustomChecklistTaskManagement })),
);
const MemoryAdminPage = lazy(() =>
  import(
    "@/modules/learningCenter/features/LearningPaths/components/Admin/MemoryAdminPage"
  ).then((m) => ({ default: m.MemoryAdminPage })),
);
const LearningPathMcpGuidePage = lazy(() =>
  import(
    "@/modules/learningCenter/features/LearningPaths/components/Admin/LearningPathMcpGuidePage"
  ).then((m) => ({ default: m.LearningPathMcpGuidePage })),
);
const TrainingCenter = lazy(() =>
  import(
    "@/modules/learningCenter/features/TrainingCenter/TrainingCenter"
  ).then((m) => ({ default: m.TrainingCenter })),
);
const EnhancementBundleBuilder = lazy(() =>
  import(
    "@/modules/learningCenter/features/TrainingSystem/components/EnhancementBundleBuilder/EnhancementBundleBuilder"
  ).then((m) => ({ default: m.EnhancementBundleBuilder })),
);
const AddOnProductList = lazy(() =>
  import(
    "@/modules/learningCenter/features/TrainingSystem/components/AddOnProductManagement/AddOnProductList"
  ).then((m) => ({ default: m.AddOnProductList })),
);
const AddOnProductEditor = lazy(() =>
  import(
    "@/modules/learningCenter/features/TrainingSystem/components/AddOnProductManagement/AddOnProductEditor"
  ).then((m) => ({ default: m.AddOnProductEditor })),
);
const ModuleLibraryPage = lazy(() =>
  import("@/modules/learningCenter/features/ModuleLibrary/pages").then((m) => ({
    default: m.ModuleLibraryPage,
  })),
);
const ModuleLibraryEditor = lazy(() =>
  import(
    "@/modules/learningCenter/features/ModuleLibrary/components/ModuleLibraryEditor"
  ).then((m) => ({ default: m.ModuleLibraryEditor })),
);
const BundleDirectory = lazy(() =>
  import(
    "@/modules/learningCenter/features/BundleDirectory/components/BundleDirectory/BundleDirectory"
  ).then((m) => ({ default: m.BundleDirectory })),
);
const BundleDetailPage = lazy(() =>
  import(
    "@/modules/learningCenter/features/BundleDirectory/components/BundleDetailPage/BundleDetailPage"
  ).then((m) => ({ default: m.BundleDetailPage })),
);
const ProgramList = lazy(() =>
  import(
    "@/modules/learningCenter/features/ProgramBuilder/components/ProgramList"
  ).then((m) => ({ default: m.ProgramList })),
);
const ProgramEditor = lazy(() =>
  import(
    "@/modules/learningCenter/features/ProgramBuilder/components/ProgramEditor"
  ).then((m) => ({ default: m.ProgramEditor })),
);
const DiscoveryHub = lazy(() =>
  import("@/modules/learningCenter/features/DiscoveryHub/DiscoveryHub").then(
    (m) => ({ default: m.DiscoveryHub }),
  ),
);
const HeroPanelAdmin = lazy(() =>
  import(
    "@/modules/learningCenter/features/HeroPanelAdmin/HeroPanelAdmin"
  ).then((m) => ({ default: m.HeroPanelAdmin })),
);
const ProgramCheckout = lazy(() =>
  import(
    "@/modules/learningCenter/features/ProgramCheckout/components/ProgramCheckout/ProgramCheckout"
  ).then((m) => ({ default: m.ProgramCheckout })),
);
const ProgramConfirmation = lazy(() =>
  import(
    "@/modules/learningCenter/features/ProgramCheckout/components/ProgramConfirmation/ProgramConfirmation"
  ).then((m) => ({ default: m.ProgramConfirmation })),
);

const ManagerTeamView = lazy(() =>
  import("@/modules/learningCenter/features/ManagerTeamView").then((m) => ({
    default: m.ManagerTeamView,
  })),
);

// Create stable component reference
/**
 * Learning-path pages render in the brand typeface (Manrope). display:contents
 * keeps the wrapper out of layout; the font still inherits through it.
 */
const BrandFontOutlet = () => (
  <div className="contents font-brand">
    <Outlet />
  </div>
);

const LearningCenterMainLayout = () => <MainLayout section="learningcenter" />;

/**
 * LearningCenterRoutes
 *
 * Routes for Learning Center administration
 * Requires ROLE_LEARNING_ADMIN or ROLE_SUPER_ADMIN permissions for admin routes
 */
const LearningCenterRoutes = (
  <Route
    element={<AuthenticationGuard component={LearningCenterMainLayout} />}
    path="/hub/learning-center"
  >
    {/* Discovery Hub - Public (all authenticated users) */}
    <Route element={<DiscoveryHub />} path="discover" />

    {/* Learning Directory - courses and self-enroll paths, one catalog */}
    <Route element={<LearningDirectory />} path="directory" />
    <Route
      element={
        <Navigate replace to="/hub/learning-center/directory?tab=courses" />
      }
      path="courses"
    />

    {/* My Courses - Learner transcript (all authenticated users) */}
    {/* My Learning - enrolled courses and learning paths, one page */}
    <Route element={<MyLearning />} path="my-learning" />
    <Route
      element={
        <Navigate replace to="/hub/learning-center/my-learning?tab=courses" />
      }
      path="my-courses"
    />

    {/* Program Catalog - Public (all authenticated users) */}
    <Route element={<BundleDirectory />} path="programs" />

    <Route element={<BundleDetailPage />} path="programs/:id" />
    {/* Program Checkout - Public (all authenticated users) */}
    <Route element={<ProgramCheckout />} path="programs/:programId/checkout" />

    <Route
      element={<ProgramConfirmation />}
      path="programs/checkout/:sessionId/confirmation"
    />

    <Route element={<CourseDetailPage />} path="courses/:id" />

    {/* Module deep links (My Courses "Continue", bookmarks) render the same
        course page, which selects the requested module from the URL. The old
        ModuleViewer read a never-populated slice and told every enrolled
        learner they weren't enrolled. */}
    <Route
      element={<CourseDetailPage />}
      path="courses/:courseId/modules/:moduleId"
    />

    {/* Learning Paths - Role-based routing handled internally */}
    <Route element={<BrandFontOutlet />}>
      <Route element={<LearningPaths />} path="paths/*" />
      {/* Path Detail Viewer - Individual path progress and items */}
      <Route element={<PathViewer />} path="my-paths/:pathId" />
    </Route>

    {/* Learning Path Viewer - Employee view of assigned paths */}
    <Route
      element={
        <Navigate replace to="/hub/learning-center/my-learning?tab=paths" />
      }
      path="my-paths"
    />

    <Route
      element={
        <Navigate replace to="/hub/learning-center/directory?tab=paths" />
      }
      path="path-directory"
    />

    {/* Training Center - Prototype */}
    <Route element={<TrainingCenter />} path="training-center" />

    {/* Manager Team View - ROLE_MANAGER, ROLE_OWNER_GM, ROLE_SUPER_ADMIN */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_MANAGER", "ROLE_OWNER_GM", "ROLE_SUPER_ADMIN"]}
        >
          <ManagerTeamView />
        </PermissionGuard>
      }
      path="manager/my-team"
    />

    {/* Coaches Learning - the coach's board, path dashboard and reviews */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_COACH", "ROLE_SUPER_ADMIN"]}>
          <CoachesLearning />
        </PermissionGuard>
      }
      path="coaches-learning"
    />

    {/* Course Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <CourseList />
        </PermissionGuard>
      }
      path="admin/courses"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <CourseEditor />
        </PermissionGuard>
      }
      path="admin/courses/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <CourseEditor />
        </PermissionGuard>
      }
      path="admin/courses/:id/edit"
    />

    {/* Skill Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <SkillList />
        </PermissionGuard>
      }
      path="admin/skills"
    />

    {/* Quiz Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <QuizList />
        </PermissionGuard>
      }
      path="admin/quizzes"
    />

    {/* Quiz Maker - Create New Quiz */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <QuizMaker />
        </PermissionGuard>
      }
      path="admin/quizzes/new"
    />

    {/* Quiz Maker - Edit Existing Quiz */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <QuizMaker />
        </PermissionGuard>
      }
      path="admin/quizzes/:id/edit"
    />

    {/* Course Category Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <CourseCategoryList />
        </PermissionGuard>
      }
      path="admin/course-categories"
    />

    {/* Badge Management - Admin Only */}
    <Route
      element={
        <PermissionGuard requiredRoles={["ROLE_SUPER_ADMIN"]}>
          <BadgeManagement />
        </PermissionGuard>
      }
      path="admin/badges/*"
    />

    {/* Custom Checklist Task Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <CustomChecklistTaskManagement />
        </PermissionGuard>
      }
      path="admin/custom-tasks/*"
    />

    {/* Memories (member + company variables) - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <MemoryAdminPage />
        </PermissionGuard>
      }
      path="admin/memories"
    />

    {/* Learning Path MCP guide - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <LearningPathMcpGuidePage />
        </PermissionGuard>
      }
      path="admin/path-mcp"
    />

    {/* Module Library Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ModuleLibraryPage />
        </PermissionGuard>
      }
      path="admin/module-library"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ModuleLibraryEditor />
        </PermissionGuard>
      }
      path="admin/module-library/create"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ModuleLibraryEditor />
        </PermissionGuard>
      }
      path="admin/module-library/:id"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ModuleLibraryEditor />
        </PermissionGuard>
      }
      path="admin/module-library/:id/edit"
    />

    {/* Enhancement Bundle Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <EnhancementBundleBuilder />
        </PermissionGuard>
      }
      path="admin/enhancement-bundles/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <EnhancementBundleBuilder />
        </PermissionGuard>
      }
      path="admin/enhancement-bundles/:bundleId/edit"
    />

    {/* Add-On Products Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <AddOnProductList />
        </PermissionGuard>
      }
      path="admin/add-on-products"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <AddOnProductEditor />
        </PermissionGuard>
      }
      path="admin/add-on-products/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <AddOnProductEditor />
        </PermissionGuard>
      }
      path="admin/add-on-products/:productId/edit"
    />

    {/* Program Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ProgramList />
        </PermissionGuard>
      }
      path="admin/programs"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ProgramEditor />
        </PermissionGuard>
      }
      path="admin/programs/new"
    />

    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <ProgramEditor />
        </PermissionGuard>
      }
      path="admin/programs/:id/edit"
    />

    {/* Hero Panel Management - Admin Only */}
    <Route
      element={
        <PermissionGuard
          requiredRoles={["ROLE_LEARNING_ADMIN", "ROLE_SUPER_ADMIN"]}
        >
          <HeroPanelAdmin />
        </PermissionGuard>
      }
      path="admin/hero-panel"
    />
  </Route>
);

export default LearningCenterRoutes;
